import axios, { AxiosError, CancelToken } from 'axios';
import { useStore } from 'components/Store';
import { parseServerError } from 'helpers/errorHelpers';
import { useCallback } from 'react';
import {
  ApplicationMethod,
  Authorization,
  Product,
  Property,
  TankInformation,
  TimingEvent,
  WorkType
} from 'screens/Applications&WorkOrders/Applications/Hooks/useApplication';
import {
  createBasePayloadBody,
  Organization,
  getRequestConfig,
  buildOperationSummaryQueryString,
  handleOperationSummaryError
} from 'screens/Applications&WorkOrders/apiHelpers';
import { ChildrenRecords } from 'components/RecordsCreatedIndicators/types';
import {
  Applicator,
  FormValues
} from 'screens/Applications&WorkOrders/StepsLayout/Constants/formikData';
import { CROPWISE_PROXY_URL } from 'utilities/apiConstants';
import {
  SORT_BY_VALUES,
  SORT_ORDER_VALUES,
  OperationSummaryParams,
  OperationSummaryResponse
} from 'components/RecordsListView/types';
import useSelectedCropSeasonIds from 'hooks/useSelectedCropSeasonIds';
import {
  LinkedRecordsResponse,
  LinkedRecordsParams
} from './linkedRecords.types';

export interface WorkOrderDetails {
  id: string;
  orgId: string;
  name: string;
  type: WorkType;
  startDate: string;
  totalCost: number | null | undefined;
  totalAppliedArea: number;
  totalAppliedAreaUnit: string;
  cropSeasonIds: string[];
  authorization: Authorization;
  notes?: string;
  properties: Property[];
  products: Product[];
  tankInformation?: TankInformation;
  createdAt: string;
  updatedAt: string;
  createdBy: string;
  updatedBy: string;
  timingEvent?: TimingEvent;
  applicationMethod?: ApplicationMethod;
  equipmentIds?: string[];
  applicators?: Applicator[];
  reiExpirationDate?: string;
  phiExpirationDate?: string;
  tagIds?: string[];
  childrenRecords?: ChildrenRecords;
}

interface WorkOrderRequest {
  id?: string;
  orgId: string;
  name: string;
  type: WorkType;
  cropSeasonIds: string[];
  notes?: string;
  startDate: string;
  timingEvent?: TimingEvent;
  authorization: Authorization;
  totalCost: number;
  totalAppliedArea: number;
  totalAppliedAreaUnit: string;
  equipmentIds?: string[];
  tagIds?: string[];
  applicators?: Array<Applicator>;
  properties: Array<Property>;
  products: Array<Product>;
  tankInformation?: TankInformation;
  applicationMethod?: ApplicationMethod;
}

export interface WorkOrderSummary {
  id: string;
  name: string;
  startDate: string;
  recordSource?: {
    recordSourceId: string;
    recordSourceType: string;
    recordSourceAdditionalInfo?: string;
  };
  childrenRecords?: ChildrenRecords;
  fieldsBrief: {
    firstFields: Array<{
      id: string;
      name: string;
    }>;
    firstFieldInfo: {
      id: string;
      name: string;
      geometry: {
        type: string;
        coordinates: any[];
      };
    };
    totalFields: number;
  };
}

export type WorkOrderSummaryResponse = OperationSummaryResponse<
  WorkOrderSummary
>;

function toPayloadBody(
  organization: Organization,
  formData: FormValues
): WorkOrderRequest {
  return createBasePayloadBody(organization, formData, {
    includeEndDate: false,
    includeWeather: false
  }) as WorkOrderRequest;
}

const useWorkOrders = () => {
  const {
    state: { organization },
    dispatch
  } = useStore();

  const { selectedCropSeasonIds } = useSelectedCropSeasonIds();
  // Bootstrap dispatches a new `organization` object reference more than once
  // (e.g. SET_ORGANIZATION then SET_USER_ENVIRONMENT) even when the id is
  // unchanged; depending on the id instead of the object keeps the summary
  // fetcher below from rebuilding and re-fetching on every one of those
  // reference changes.
  const organizationId = organization?.id;

  const createWorkOrder = useCallback(
    async (formData: FormValues) => {
      const body: WorkOrderRequest = toPayloadBody(organization, formData);
      const config = getRequestConfig(organization.id);

      const response = await axios.post<WorkOrderDetails>(
        `${CROPWISE_PROXY_URL}/v1/base/work-orders`,
        body,
        config
      );
      return response.data;
    },
    [organization]
  );

  const editWorkOrder = useCallback(
    async (workOrderId: string, formData: FormValues) => {
      const body: WorkOrderRequest = toPayloadBody(organization, formData);
      const config = getRequestConfig(organization.id);

      const response = await axios.put<WorkOrderDetails>(
        `${CROPWISE_PROXY_URL}/v1/base/work-orders/${workOrderId}`,
        body,
        config
      );
      return response.data;
    },
    [organization]
  );

  const getWorkOrder = useCallback(
    async (
      workOrderId: string,
      cancelToken?: CancelToken
    ): Promise<WorkOrderDetails> => {
      const config = getRequestConfig(organization.id);

      if (cancelToken) {
        config.cancelToken = cancelToken;
      }

      let response;
      try {
        response = await axios.get<WorkOrderDetails>(
          `${CROPWISE_PROXY_URL}/v1/base/work-orders/${workOrderId}`,
          config
        );
      } catch (error) {
        if (!axios.isCancel(error)) {
          parseServerError(dispatch)(error);
        }
        throw error;
      }

      return response.data;
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [organization]
  );

  const getSummaryWorkOrders = useCallback(
    async (
      {
        page = 0,
        size = 50,
        fromDate,
        toDate,
        sorts = [
          {
            sortBy: SORT_BY_VALUES.CREATION_DATE,
            order: SORT_ORDER_VALUES.DESC
          },
          { sortBy: SORT_BY_VALUES.NAME, order: SORT_ORDER_VALUES.ASC }
        ],
        filters = {
          name: ''
        },
        id,
        cropZoneIds,
        cropZoneMatchType
      }: Partial<OperationSummaryParams> = {},
      cancelToken?: CancelToken
    ): Promise<WorkOrderSummaryResponse> => {
      let response;
      try {
        const queryString = buildOperationSummaryQueryString(
          {
            page,
            size,
            fromDate,
            toDate,
            sorts,
            filters,
            id,
            cropZoneIds,
            cropZoneMatchType
          },
          selectedCropSeasonIds
        );
        const url = `${CROPWISE_PROXY_URL}/v1/base/work-orders/summary${
          queryString ? `?${queryString}` : ''
        }`;

        response = await axios.get(
          url,
          getRequestConfig(organizationId, cancelToken)
        );
      } catch (error) {
        if (!axios.isCancel(error)) {
          parseServerError(dispatch)(error);
        }

        response = handleOperationSummaryError<WorkOrderSummary>(
          error as AxiosError
        );
      }

      return response.data;
    },
    [organizationId, selectedCropSeasonIds, dispatch]
  );

  const deleteWorkOrder = useCallback(
    async (workOrderId: string) => {
      const url = `${CROPWISE_PROXY_URL}/v1/base/work-orders/${workOrderId}`;
      const config = getRequestConfig(organization.id);

      let response;

      try {
        response = await axios.delete(url, config);
      } catch (error) {
        if (!axios.isCancel(error)) {
          parseServerError(dispatch)(error);
        }
        throw error;
      }

      return response.data;
    },
    [dispatch, organization.id]
  );

  /**
   * Fetches the records created from a Work Order.
   *
   * Nested under the parent rather than filtering `/applications/summary`, so the
   * cost stays bounded by one parent's children. No crop-season filter: a child
   * may sit in a different season, and scoping would silently hide it.
   */
  const getLinkedRecords = useCallback(
    async (
      { workOrderId, page, size }: LinkedRecordsParams,
      cancelToken?: CancelToken
    ): Promise<LinkedRecordsResponse> => {
      const config = getRequestConfig(organization.id, cancelToken);

      const searchParams = new URLSearchParams({
        page: String(page),
        size: String(size),
        // Singular `sort` — `sorts` is silently ignored by Spring's resolver.
        sort: `${SORT_BY_VALUES.START_DATE},${SORT_ORDER_VALUES.DESC}`
      });

      try {
        const response = await axios.get<LinkedRecordsResponse>(
          `${CROPWISE_PROXY_URL}/v1/base/work-orders/${workOrderId}/linked-records?${searchParams.toString()}`,
          config
        );
        return response.data;
      } catch (error) {
        if (!axios.isCancel(error)) {
          parseServerError(dispatch)(error);
        }
        throw error;
      }
    },
    [organization.id, dispatch]
  );

  return {
    createWorkOrder,
    editWorkOrder,
    getWorkOrder,
    getLinkedRecords,
    getSummaryWorkOrders,
    deleteWorkOrder
  };
};
export default useWorkOrders;
